import { useEffect, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useReloadGreeting } from '@/hooks/api/useGreetings'

// Helper to read config.greetings.* (same shape GreetingSection uses)
function getGreetings(config: Record<string, unknown> | undefined): Record<string, unknown> {
  return (config?.greetings ?? {}) as Record<string, unknown>
}

/**
 * The text a voice preview should speak for the current assistant form state.
 *
 * When the assistant uses a dynamic greeting (config.greetings.dynamic_enabled)
 * the static `greeting` field is not what callers hear, so the resolved
 * greeting is fetched from the backend — the same preview GreetingSection
 * shows on the Personality tab. While that preview is loading or failed, the
 * static greeting is used instead. Returns `undefined` only when there is no
 * greeting at all; callers then omit `text` so the backend picks a localized
 * sample.
 */
export function usePlayableGreeting(): string | undefined {
  const { control } = useFormContext()
  const config = useWatch({ control, name: 'config' }) as Record<string, unknown> | undefined
  const greeting = useWatch({ control, name: 'greeting' }) as string
  const defaultLanguage = useWatch({ control, name: 'default_language' }) as string
  const additionalLanguages = useWatch({ control, name: 'additional_languages' }) as string[]
  const assistantName = useWatch({ control, name: 'name' }) as string

  const greetings = getGreetings(config)
  const isDynamic = Boolean(greetings.dynamic_enabled)
  const style = (greetings.style as string) || 'simple_direct'
  const personaName = (greetings.persona_name as string) || 'Bonnie'

  // mutateAsync is referentially stable in TanStack Query v5, so it is safe
  // (and lint-clean) as an effect dependency.
  const { mutateAsync: reloadGreeting } = useReloadGreeting()
  const [dynamicText, setDynamicText] = useState('')

  useEffect(() => {
    if (!isDynamic) return undefined
    let cancelled = false
    reloadGreeting({
      dynamic_enabled: true,
      style,
      language: defaultLanguage,
      base_greeting: greeting,
      assistant_name: assistantName,
      persona_name: personaName,
      additional_languages: additionalLanguages,
    })
      .then((result) => {
        if (cancelled) return
        const combined = result.ivr_language_message
          ? `${result.ivr_language_message}\n\n${result.greeting}`
          : result.greeting
        setDynamicText(combined)
      })
      .catch((error) => {
        console.error('Failed to load greeting preview:', error)
      })
    return () => {
      cancelled = true
    }
  }, [
    isDynamic,
    style,
    defaultLanguage,
    additionalLanguages,
    personaName,
    greeting,
    assistantName,
    reloadGreeting,
  ])

  if (isDynamic) return dynamicText || greeting || undefined
  return greeting || undefined
}
